import { Refine } from "@pankod/refine-core";
import routerProvider, { Navigate } from "@pankod/refine-react-router-v6";

import {
  notificationProvider,
  Layout,
  ReadyPage,
  ErrorComponent,
  ConfigProvider,
} from "@pankod/refine-antd";
import "@pankod/refine-antd/dist/styles.min.css";
import dataProvider from "@pankod/refine-simple-rest";
import axios, { AxiosInstance, AxiosRequestConfig } from "@pankod/refine-simple-rest/node_modules/axios";
import { authProvider } from "./providers/auth-provider.provider";
import { Login } from "pages/login";
import { DashboardPage } from "pages/dashboard";
import { Sider } from "components/organisms/sider.component";

import './sass/main.scss';

import { API_URL, PERMISSIONS_KEY, TOKEN_KEY } from "constants/environment";
import { accessControlProvider } from "providers/access-control.provider";
import { useState } from "react";
import moment from "moment-timezone";
import locale from "antd/lib/locale/es_ES";
import { SettingFilled, UserOutlined, FileDoneOutlined, BookOutlined, BarChartOutlined } from "@ant-design/icons";
import { CustomPage } from "pages/custom/custom-page";
import { UserCreate, UserEdit, UserList, UserShow } from "pages/users";
import { PermissionsList } from "pages/permissions/list";
import { AttendantCreate, AttendantEdit, AttendantList } from "pages/attendants";
import { EventCreate, EventEdit, EventListPage } from "pages/events";
import { InvoiceTypeCreate, InvoiceTypeEdit, InvoiceTypeList } from "pages/invoice-types";
import { EventTypeCreate, EventTypeEdit, EventTypeList } from "pages/event-types";
import { AssignInvoices } from "pages/custom/assign-invoices";

import { WorkPlanList, WorkPlanCreate, WorkPlanEdit, WorkPlanCalendarPage, WorkPlanSummary } from "pages/work-plan";
import { RentPlaceList, RentPlaceCreate, RentPlaceEdit, RentPlaceCalendarPage } from "pages/rent-places";

const axiosInstance: AxiosInstance = axios.create();

axiosInstance.interceptors.request.use((request: AxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        if (request.headers) {
            request.headers["Authorization"] = `Bearer ${token}`;
        } else {
            request.headers = {
                Authorization: `Bearer ${token}`,
            };
        }
    }
    return request;
});



function App() {
  const lsPermissions = localStorage.getItem(PERMISSIONS_KEY);
  const [permissions, setPermissions] = useState(lsPermissions || '');
  moment.tz.setDefault('America/Mazatlan');
  
  return (
    <ConfigProvider locale={locale}>
      <Refine
      notificationProvider={notificationProvider}
      Layout={Layout}
      Sider={Sider}
      ReadyPage={ReadyPage}
      catchAll={<ErrorComponent />}
      routerProvider={{
        ...routerProvider,
        routes: [
          {
            element: <CustomPage />,
            path: '/custom'
          },
          {
            element: <WorkPlanCalendarPage />,
            path: '/work-plans/calendar',
            layout: true,
          },
          {
            element: <WorkPlanSummary />,
            path: '/work-plans/summary',
            layout: true,
          },
          {
            element: <RentPlaceCalendarPage />,
            path: '/rent-places/calendar',
            layout: true,
          },
        ] as typeof routerProvider.routes,
      }}
      accessControlProvider={accessControlProvider(permissions)}
      dataProvider={dataProvider(API_URL, axiosInstance)}
      authProvider={authProvider(axiosInstance, setPermissions)}
      LoginPage={Login}
      DashboardPage={() => <Navigate to="/events" />}
      reactQueryDevtoolConfig={false}
      resources={[
        {
          parentName: "",
          name: "events",
          icon: <UserOutlined />,
          list: EventListPage,
          create: EventCreate,
          edit: EventEdit,
          options: {
            label: "Eventos",
            route: "events"
          },
        },
        {
          name: "work-plans",
          icon: <FileDoneOutlined />,
          list: WorkPlanList,
          create: WorkPlanCreate,
          edit: WorkPlanEdit,
          options: {
            label: "Plan de Trabajo",
            route: "work-plans",
          },
        },
        {
          name: "work-plans-summary",
          icon: <BarChartOutlined />,
          list: WorkPlanSummary,
          options: {
            label: "Resumen Plan de Trabajo",
            route: "work-plans-summary"
          },
        },
        {
          name: "rent-places",
          icon: <BookOutlined />,
          list: RentPlaceList,
          create: RentPlaceCreate,
          edit: RentPlaceEdit,
          options: {
            label: "Renta de Lugares",
            route: "rent-places",
          },
        },
        {
          name: "cruds",
          icon: <SettingFilled />,
          options: {
            label:"Configuración",
          }
        },
        {
          parentName: "cruds",
          name: "event-types",
          icon: <UserOutlined />,
          create: EventTypeCreate,
          list: EventTypeList,
          edit: EventTypeEdit,
          options: {
            label: "Tipos de eventos",
            route: "event-types"
          },
        },
        {
          parentName: "cruds",
          name: "attendants",
          icon: <UserOutlined />,
          create: AttendantCreate,
          list: AttendantList,
          edit: AttendantEdit,
          options: {
            label: "Atienden",
            route: "attendants"
          },
        },
        {
          parentName: "cruds",
          name: "invoice-types",
          icon: <UserOutlined />,
          create: InvoiceTypeCreate,
          list: InvoiceTypeList,
          edit: InvoiceTypeEdit,
          options: {
            label: "Tipos de factura",
            route: "invoice-types"
          },
        },
        {
          parentName: "cruds",
          name: "users-bo",
          icon: <UserOutlined />,
          create: UserCreate,
          list: UserList,
          show: UserShow,
          edit: UserEdit,
          options: {
            label: "Usuarios Adm",
            route: "users-bo"
          },
        }
      ]}
    />
    </ConfigProvider>
  );
}

export default App;
